import React, { useMemo, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import { Tag } from "primereact/tag";
import { ProgressBar } from "primereact/progressbar";
import { Button } from "primereact/button";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import {
  useGetSupportCategoriesQuery,
  useGetSupportFeedbackDashboardQuery,
} from "../../store/services/support";
import { SupportArticleDetailDialog } from "../../components/Dialogs/SupportArticleDetailDialog";
import { SupportFeedbackDashboardItem } from "../../models/support";

type ReviewFilter = "all" | "review" | "ok";

const reviewOptions: { label: string; value: ReviewFilter }[] = [
  { label: "Todos", value: "all" },
  { label: "Requiere revisión", value: "review" },
  { label: "Sin observaciones", value: "ok" },
];

// Búsqueda tolerante a mayúsculas y acentos: "articulo" tiene que encontrar "Artículo".
const normalizeText = (value: string): string =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

export function SupportFeedbackDashboard() {
  const [titleSearch, setTitleSearch] = useState<string>("");
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [reviewFilter, setReviewFilter] = useState<ReviewFilter>("all");
  const [articleToView, setArticleToView] = useState<{
    id: string;
    categoryName: string | null;
  }>();

  const { data: categories, isLoading: isLoadingCategories } = useGetSupportCategoriesQuery();
  const { data: items, isLoading, isFetching } = useGetSupportFeedbackDashboardQuery(
    categoryId ? { categoryId } : undefined
  );

  const categoryOptions = useMemo(
    () => (categories ?? []).map((c) => ({ label: c.name, value: c.id })),
    [categories]
  );

  const pctBodyTemplate = (row: SupportFeedbackDashboardItem) =>
    row.feedbackPct === null ? (
      <span className="text-500">Sin votos</span>
    ) : (
      <div className="flex align-items-center gap-2">
        <ProgressBar
          value={row.feedbackPct}
          showValue={false}
          style={{ height: "0.75rem", width: "10rem" }}
        />
        <span className="text-700 font-medium">{row.feedbackPct}%</span>
      </div>
    );

  const filteredItems = useMemo(() => {
    const term = normalizeText(titleSearch);
    return (items ?? []).filter((item) => {
      if (term && !normalizeText(item.title).includes(term)) return false;
      if (reviewFilter === "review" && !item.needsReview) return false;
      if (reviewFilter === "ok" && item.needsReview) return false;
      return true;
    });
  }, [items, titleSearch, reviewFilter]);

  const detailBodyTemplate = (row: SupportFeedbackDashboardItem) => (
    <Button
      icon="pi pi-eye"
      rounded
      outlined
      size="small"
      tooltip="Ver detalle"
      tooltipOptions={{ position: "top" }}
      onClick={() => setArticleToView({ id: row.articleId, categoryName: row.categoryName })}
    />
  );

  const reviewBodyTemplate = (row: SupportFeedbackDashboardItem) =>
    row.needsReview ? (
      <Tag severity="danger" value="Requiere revisión" />
    ) : (
      <span className="text-500">—</span>
    );

  return (
    <div className="p-card gap-4 p-3">
      <div className="flex w-full justify-content-center align-items-center gap-3 mt-4 mb-6">
        <h1 className="text-center m-0">Feedback de artículos</h1>
      </div>

      <div className="flex flex-wrap align-items-end gap-3 mb-4">
        <div className="flex flex-column gap-2">
          <label htmlFor="titleSearch" className="text-600 text-sm">
            Buscar
          </label>
          <IconField iconPosition="left" className="w-full">
            <InputIcon className="pi pi-search" />
            <InputText
              id="titleSearch"
              value={titleSearch}
              onChange={(e) => setTitleSearch(e.target.value)}
              placeholder="Buscar por título"
              className="w-full"
              style={{ minWidth: "14rem" }}
            />
          </IconField>
        </div>
        <div className="flex flex-column gap-2">
          <label htmlFor="reviewFilter" className="text-600 text-sm">
            Estado
          </label>
          <Dropdown
            id="reviewFilter"
            value={reviewFilter}
            options={reviewOptions}
            optionLabel="label"
            optionValue="value"
            onChange={(e) => setReviewFilter(e.value)}
            style={{ minWidth: "14rem" }}
          />
        </div>
        <div className="flex flex-column gap-2">
          <label htmlFor="categoryFilter" className="text-600 text-sm">
            Categoría
          </label>
          <Dropdown
            id="categoryFilter"
            value={categoryId}
            options={categoryOptions}
            optionLabel="label"
            optionValue="value"
            loading={isLoadingCategories}
            onChange={(e) => setCategoryId(e.value ?? null)}
            placeholder="Todas las categorías"
            showClear
            style={{ minWidth: "16rem" }}
          />
        </div>
      </div>

      <DataTable
        value={filteredItems}
        loading={isLoading || isFetching}
        tableStyle={{ minWidth: "60rem" }}
        paginator
        rows={10}
        rowsPerPageOptions={[5, 10, 25, 50]}
        responsiveLayout="scroll"
        emptyMessage={
          titleSearch.trim() || reviewFilter !== "all"
            ? "No hay artículos que coincidan con los filtros."
            : "No hay datos de utilidad para mostrar."
        }
      >
        <Column field="title" header="Título" style={{ minWidth: "18rem" }} />
        <Column
          header="Categoría"
          body={(row: SupportFeedbackDashboardItem) => row.categoryName || "—"}
          style={{ width: "12rem" }}
        />
        <Column field="feedbackYes" header="Sí" style={{ width: "6rem" }} />
        <Column field="feedbackNo" header="No" style={{ width: "6rem" }} />
        <Column header="% Útil" body={pctBodyTemplate} style={{ width: "16rem" }} />
        <Column header="Estado" body={reviewBodyTemplate} style={{ width: "12rem" }} />
        <Column header="Detalle" body={detailBodyTemplate} style={{ width: "7rem" }} />
      </DataTable>

      <SupportArticleDetailDialog
        articleId={articleToView?.id}
        fallbackCategoryName={articleToView?.categoryName}
        onHide={() => setArticleToView(undefined)}
      />
    </div>
  );
}
